import { Button, InputNumber, Spin, Tooltip } from 'antd';
import classNames from 'classnames';
import { orderBy } from 'lodash';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    QUANTITY,
    TOOLTIP_PLACEMENT,
    TRENDING_FLAG,
    UNIT_MEASURE,
    sortAttAsc,
    sortAttDesc,
    sortAttName,
    sortAttPrice,
} from '@common/constants';
import cssColors from '@common/css/cssColors.module.scss';
import { FaucetOptions, SortBy } from '@common/enums';
import { getOnTrendLabelFromProduct } from '@common/helpers';
import formatUOM from '@common/helpers/formatUOM';
import { ApplianceModel, SelectedItemProperties } from '@common/models';
import {
    COMING_SOON_IMAGE,
    getProductDescription,
    handleIntegerNumberInput,
    isFaucetOptionEnabled,
} from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import { Filters, SectionSubtitle, SortByMenu } from '@core/components';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    selectFaucetSortBy,
    selectSelectedFaucets,
    updateFaucetFilters,
    updateFaucetsSortBy,
    updateSelectedFaucets,
} from '@redux/slices/faucetSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import TrashIcon from '../../../../../../public/assets/icons/trash.svg';
import useFaucets from './useFaucets';
import useFilteredFaucets from './useFilteredFaucets';

import './faucets.scss';

interface FaucetItemProps {
    faucet: ApplianceModel;
}

interface FaucetQuantityProps {
    faucet: ApplianceModel;
    quantity: number;
    unitOfMeasure: string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const Faucets = (): JSX.Element | null => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { browsedFaucets, isFetchingFaucets } = useFaucets();

    const selectedFaucets = useAppSelector(selectSelectedFaucets);
    const { faucetFilters } = useFilteredFaucets();
    const [isFilterExpanded, setFilterExpanded] = useState(false);
    const zipcode = useAppSelector(selectZipcode);
    const faucetSortBy = useAppSelector(selectFaucetSortBy);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();

    const [selectedOption, setSelectedOption] = useState<string>('');
    const [, setSelectedFaucet] = useState<ApplianceModel | undefined>();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const { retailerFileSettings } = useRetailerSettings();

    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    const ref = useRef<HTMLDivElement | null>(null);

    const faucetSortOptions = [SortBy.TrendingNow, SortBy.LowToHigh, SortBy.HighToLow, SortBy.NameAZ, SortBy.NameZA];

    const updateSelectedFaucet = (faucetOption: FaucetOptions) => {
        if (faucetOption === selectedOption) {
            setSelectedOption('');
            return;
        }

        if (faucetOption === FaucetOptions.NONE && selectedFaucets.size > 0) {
            setIsModalVisible(true);
        }

        setSelectedOption(faucetOption);
    };

    const selectFaucet = (faucet: ApplianceModel) => {
        setSelectedFaucet(faucet);

        const selectedFaucetsMap = new Map(selectedFaucets);

        const currentSelectedFaucet = selectedFaucets.get(faucet.productId);
        if (currentSelectedFaucet) {
            if (currentSelectedFaucet.isManuallyChanged) {
                selectedFaucetsMap.set(faucet.productId, {
                    quantity: currentSelectedFaucet.quantity,
                    isSelected: !currentSelectedFaucet.isSelected,
                    isManuallyChanged: currentSelectedFaucet.isManuallyChanged,
                });
            } else {
                selectedFaucetsMap.delete(faucet.productId);
            }

            dispatch(updateSelectedFaucets(selectedFaucetsMap));
            return;
        }

        selectedFaucetsMap.set(faucet.productId, {
            quantity: 0,
            isSelected: true,
            isManuallyChanged: false,
        });

        dispatch(updateSelectedFaucets(selectedFaucetsMap));
    };

    const FaucetsOptions = ({ selectedOption }: { selectedOption: string }): JSX.Element => (
        <div ref={ref} className="appliance-options">
            {Object.entries(FaucetOptions).map(([faucetOption, faucetOptionValue], index) =>
                isFaucetOptionEnabled(retailerFileSettings, faucetOptionValue) ? (
                    <Button
                        key={index}
                        className={classNames({ selected: faucetOptionValue === selectedOption })}
                        onClick={() => updateSelectedFaucet(faucetOptionValue)}
                    >
                        {t(`SCROLLABLE_AREA.FAUCET.OPTIONS.${faucetOption}`)}
                    </Button>
                ) : null
            )}
        </div>
    );

    const sortFaucets = (faucets: ApplianceModel[]) => {
        switch (faucetSortBy) {
            case SortBy.HighToLow:
                return orderBy(faucets, [sortAttPrice], [sortAttDesc]);
            case SortBy.LowToHigh:
                return orderBy(faucets, [sortAttPrice], [sortAttAsc]);
            case SortBy.NameAZ:
                return orderBy(faucets, [sortAttName], [sortAttAsc]);
            case SortBy.NameZA:
                return orderBy(faucets, [sortAttName], [sortAttDesc]);
            case SortBy.TrendingNow: {
                const onTrendSinks = faucets.filter((faucet: ApplianceModel) =>
                    faucet.features.find((item) => item.name.match(TRENDING_FLAG))
                );

                const otherSinks = faucets.filter(
                    (faucet: ApplianceModel) =>
                        !faucet.features.find((item) => item.name.match(TRENDING_FLAG)) && faucet
                );

                return [...onTrendSinks, ...otherSinks];
            }
            default:
                return orderBy(faucets, [sortAttName], [sortAttAsc]);
        }
    };

    const FaucetsContent = ({ selectedOption }: { selectedOption: string }): JSX.Element => {
        const isSelectedOptionBrowseFaucets = selectedOption === FaucetOptions.BROWSE;

        // loading same mock faucets for supply or brose option (@TODO fetch faucets for each option when the api is done)
        const browseFaucets = isSelectedOptionBrowseFaucets ? browsedFaucets : [];

        return (
            <>
                <FaucetsOptions selectedOption={selectedOption} />
                {isFetchingFaucets && <Spin style={{ fontSize: 24 }} />}
                {isSelectedOptionBrowseFaucets ? (
                    <div className="title title-with-sort">
                        {t('SCROLLABLE_AREA.FAUCET.BROWSE_FAUCET_INFO')}
                        <SortByMenu
                            sortBy={faucetSortBy}
                            setSortBy={(newSortby) => {
                                dispatch(updateFaucetsSortBy(newSortby));
                            }}
                            options={faucetSortOptions}
                        />
                    </div>
                ) : null}

                <div className={classNames('faucet-card-wrapper', { 'row-direction': isFilterExpanded })}>
                    {isSelectedOptionBrowseFaucets ? (
                        <Filters
                            isFilterExpanded={isFilterExpanded}
                            setFilterExpanded={setFilterExpanded}
                            filterOptions={{ ...faucetFilters }}
                            updateFilters={(filters) => dispatch(updateFaucetFilters(filters))}
                        />
                    ) : null}

                    <div className="faucet-group-wrapper">
                        <div className="card-appliance">
                            <FaucetsList faucets={browseFaucets} />
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const FaucetsList = ({ faucets }: { faucets: ApplianceModel[] }): JSX.Element => (
        <>
            {sortFaucets(faucets).map((faucet) => (
                <FaucetItem key={faucet.productId} faucet={faucet} />
            ))}
        </>
    );

    const updateQuantity = (faucet: ApplianceModel, newQuantity: number) => {
        if (newQuantity < 0) {
            return;
        }

        const newMap = new Map(selectedFaucets);

        newMap.set(faucet.productId, {
            quantity: newQuantity,
            isSelected: (newMap.get(faucet.productId) as SelectedItemProperties).isSelected && newQuantity > 0,
            isManuallyChanged: true,
        });

        dispatch(updateSelectedFaucets(newMap));
    };

    const changeProductQuantity = (faucet: ApplianceModel, amount: number): void => {
        const newMap = new Map(selectedFaucets);
        const prevCutOutValue = newMap.get(faucet.productId) as SelectedItemProperties;
        const updatedQuantity = prevCutOutValue.quantity + amount;

        if (updatedQuantity > -1) {
            newMap.set(faucet.productId, {
                quantity: updatedQuantity,
                isSelected: prevCutOutValue.isSelected && updatedQuantity > 0,
                isManuallyChanged: true,
            });

            dispatch(updateSelectedFaucets(newMap));
        }
    };

    const FaucetQuantity = ({ faucet, quantity, unitOfMeasure }: FaucetQuantityProps): JSX.Element => {
        const [productQuantity, setProductQuantity] = useState(quantity);

        return (
            <div className="faucet-quantity">
                <Button
                    type={'text'}
                    style={{ color: productQuantity === 0 ? 'transparent' : 'inherit' }}
                    disabled={productQuantity === 0}
                    onClick={(e) => {
                        e.stopPropagation();
                        changeProductQuantity(faucet, -1);
                    }}
                >
                    ${QUANTITY.MINUS}
                </Button>
                <InputNumber
                    value={quantity}
                    onKeyDown={handleIntegerNumberInput}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(value) => setProductQuantity(Math.floor(value ?? 0))}
                    onPressEnter={() => updateQuantity(faucet, productQuantity)}
                    onBlur={() => updateQuantity(faucet, productQuantity)}
                />
                {unitOfMeasure}
                <Button
                    type={'text'}
                    onClick={(e) => {
                        e.stopPropagation();
                        changeProductQuantity(faucet, 1);
                    }}
                >
                    {QUANTITY.PLUS}
                </Button>
            </div>
        );
    };

    const FaucetItem = ({ faucet }: FaucetItemProps): JSX.Element => {
        const description = getProductDescription(faucet); // faucet.consumerDesc;
        const isSelected = selectedFaucets.get(faucet.productId)?.isSelected;

        const unitOfMeasure = formatUOM(faucet.unitOfMeasure || UNIT_MEASURE.FAUCET);
        const onTrendLabel = getOnTrendLabelFromProduct(faucet);

        return (
            <div
                className={classNames('card-option multi-action-card', 'faucet-card-content', {
                    selected: isSelected,
                })}
                onClick={() => {
                    selectFaucet(faucet);
                }}
            >
                {onTrendLabel && <div className="on-trend">{onTrendLabel}</div>}
                <img
                    alt={'faucet'}
                    className="card-option__image"
                    src={faucet.imageUrl || COMING_SOON_IMAGE}
                    draggable="true"
                    crossOrigin="anonymous"
                />
                <Tooltip title={description} color={cssColors.toolTipColor} placement={TOOLTIP_PLACEMENT}>
                    <div className="name">{description}</div>
                </Tooltip>
                <div className="price">{formatPriceUnitOrFree(faucet.price, unitOfMeasure)}</div>
                {isSelected ? (
                    <FaucetQuantity
                        faucet={faucet}
                        quantity={selectedFaucets.get(faucet.productId)?.quantity ?? 0}
                        unitOfMeasure={unitOfMeasure}
                    />
                ) : null}
            </div>
        );
    };

    const onConfirm = () => {
        dispatch(updateSelectedFaucets(new Map()));
        setIsModalVisible(false);
    };

    const onCancel = () => {
        setIsModalVisible(false);
    };

    const areFaucetsAvailable = zipcode && selectedStore && selectedColor && areThereShapesInTheDrawingArea;

    if (!areFaucetsAvailable) {
        return null;
    }

    return (
        <>
            <SectionSubtitle
                name={t('SCROLLABLE_AREA.FAUCET.TITLE')}
                description={t('SCROLLABLE_AREA.FAUCET.DESCRIPTION')}
                showLine
            />
            <FaucetsContent selectedOption={selectedOption} />
            <ConfirmationDialog
                visible={isModalVisible}
                onCancel={onCancel}
                onConfirm={onConfirm}
                icon={<TrashIcon />}
                title={t('SCROLLABLE_AREA.FAUCET.CONFIRMATION_MESSAGE')}
                content={t('SCROLLABLE_AREA.FAUCET.CHANGE_WARNING')}
            />
        </>
    );
};

export default Faucets;
